/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import {getConfig as getAppConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';

import SSRCacheMgr from 'coreClasses/cache/SSRCacheMgr';
import DeviceMgr from 'coreClasses/device/DeviceMgr';
import {Product} from 'coreStore/data/products/types';
import {ProductSearchHit} from 'coreStore/data/productSearchResult/interface';
import {getServerFetch} from 'coreUtils/adapters/fetch';
import {getAssetUrl as pwaKitGetAssetUrl} from 'coreUtils/adapters/sdk';
import {getDefaultSite, getSites, resolveSiteFromUrl} from 'coreUtils/siteUtils';
import {parseURL} from 'coreUtils/url';

const scriptLoadingPromises = {};
const maxConcurrentImagesLoads = 5;
const imagesLoadQueue = [];

let cachedConfig;
let cachedFrontendConfig;
let currentImagesLoad = 0;

const removeNestedProperty = (obj, path) => {
	const properties = path.split('.');
	const lastProperty = properties.pop();

	const nestedObj = properties.reduce((acc, prop) => {
		if (acc && prop in acc) {
			acc[prop] = structuredClone(acc[prop]);

			return acc[prop];
		}

		return undefined;
	}, obj);

	if (nestedObj && lastProperty in nestedObj) {
		delete nestedObj[lastProperty];
	}
};

export const getConfig = () => {
	if (!cachedConfig) {
		try {
			cachedConfig = JSON.parse(JSON.stringify(getAppConfig()));
		} catch (e) {
			cachedConfig = getAppConfig();
		}
	}

	return cachedConfig;
};

export const getFrontEndConfig = () => {
	if (!cachedFrontendConfig) {
		const propertiesToRemove = [
			'app.cmsAPI.parameters.contentVersion',
			// Server only (CPA proxy): never sent to the browser even if a config still sets it
			'app.cmsAPI.parameters.previewToken',
			'app.apiSpecificationPath'
		];

		try {
			cachedFrontendConfig = JSON.parse(JSON.stringify(getAppConfig()));
		} catch (e) {
			cachedFrontendConfig = getAppConfig();
		}

		propertiesToRemove.forEach(property => removeNestedProperty(cachedFrontendConfig, property));
	}

	return cachedFrontendConfig;
};

/**
 * Returns true if is loaded in iframe
 */
export const isInIframe = () => {
	if (isServer) {
		return false;
	}

	try {
		return window.self !== window.top;
	} catch (e) {
		return true;
	}
};

/**
 * Call requestIdleCallback in supported browsers.
 *
 * https://developers.google.com/web/updates/2015/08/using-requestidlecallback
 * http://caniuse.com/#feat=requestidlecallback
 */
export const requestIdleCallback = (fn) => {
	if ('requestIdleCallback' in window) {
		return window.requestIdleCallback(fn);
	} else {
		return fn();
	}
};

export const cancelIdleCallback = (id) => {
	if ('cancelIdleCallback' in window) {
		return window.cancelIdleCallback(id);
	} else {
		return clearTimeout(id);
	}
};

export const watchOnlineStatus = (callback, win = window) => {
	const off = () => callback(false);
	const on = () => callback(true);
	win.addEventListener('offline', off);
	win.addEventListener('online', on);

	const unsubscribe = () => {
		win.removeEventListener('offline', off);
		win.removeEventListener('online', on);
	};

	return unsubscribe;
};

/**
 * Compares the primary fields of two address objects to determine if they match.
 * @param {Object} addr1
 * @param {Object} addr2
 * @returns {boolean}
 */
export const isMatchingAddress = (addr1, addr2) => {
	const normalize = (addr) => {
		// eslint-disable-next-line no-unused-vars
		const {id, addressId, _type, preferred, creationDate, lastModified, ...normalized} = addr;

		return normalized;
	};

	return shallowEquals(normalize(addr1), normalize(addr2));
};

/**
 * Performs a shallow comparison on two objects
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 */
export const shallowEquals = (a, b) => {
	for (let key in a) {
		if (!(key in b) || a[key] !== b[key]) {
			return false;
		}
	}

	for (let key in b) {
		if (!(key in a) || a[key] !== b[key]) {
			return false;
		}
	}

	return true;
};

/**
 * No operation function. You can use this
 * empty function when you wish to pass
 * around a function that will do nothing.
 * Usually used as default for event handlers.
 */
export const noop = () => {};

/**
 * Flattens a tree data structure into an array.
 * @param {Object} node - object
 * @returns
 */
export const flattenCategoriesTree = (node: any) => {
	const childKey = 'categories';
	const parentKey = 'parentCategoryTree';
	const nodesById = {};
	const stack = [{node}];

	while (stack.length) {
		const {node: currentNode} = stack.pop();

		nodesById[currentNode.id] = {
			...currentNode,
			[childKey]: currentNode[childKey] ? currentNode[childKey].map((child) => child.id) : [],
			[parentKey]: currentNode[parentKey] ? currentNode[parentKey].map((parent) => parent.id) : [],
		};

		const children = currentNode[childKey];

		if (children && children.length) {
			children.forEach((childNode) => {
				stack.push({node: childNode});
			});
		}
	}

	return nodesById;
};

export const flattenArray = (arr) => {
	var newArr = [];

	for (var i = 0; i < arr.length; i++) {
		if (Array.isArray(arr[i])) {
			newArr = newArr.concat(flattenArray(arr[i]));
		} else {
			newArr.push(arr[i]);
		}
	}

	return newArr;
};

/**
 * Check the current execution environment
 * is client side or server side
 * @returns Boolean
 */
export const isServer = typeof window === 'undefined';

/**
 * retrieves an item from session storage
 * @param {string} key
 * @returns JSON | undefined
 */
export const getSessionJSONItem = (key) => {
	if (isServer) {
		return undefined;
	}

	const item = window.sessionStorage.getItem(key);

	if (item) {
		return JSON.parse(item);
	} else {
		return undefined;
	}
};

/**
 * sets an item in session storage
 * @param {string} key
 * @param {string} value
 */
export const setSessionJSONItem = (key, value) => {
	window.sessionStorage.setItem(key, JSON.stringify(value));
};

/**
 * clears an item in session storage
 * @param {string} key
 */
export const clearSessionJSONItem = (key) => {
	window.sessionStorage.removeItem(key);
};

/**
 * Makes bold a substring of a string by adding <b> tags
 * @param {string} str
 * @param {string} substr
 * @returns stringified HTML Node
 */
export const boldString = (str, substr) => {
	return str.replace(RegExp(substr, 'g'), `<b>${substr}</b>`);
};

/**
 * Capitalizes the words in a string
 * @param {string} text
 * @returns capitalized text
 */
export const capitalize = (text) => {
	return text
		?.toLowerCase()
		?.split(' ')
		?.map((s) => s?.charAt(0)?.toUpperCase() + s?.substring(1))
		?.join(' ');
};

/**
 * This function return the identifiers (site and locale) from the given url
 * The site will always go before locale if both of them are presented in the pathname
 * @param path {string}
 * @returns {{siteRef: string, localeRef: string}} - site and locale reference (it could either be id or alias)
 */
export const getParamsFromPath = (path) => {
	const {pathname, search} = parseURL(path);
	const config = getConfig();
	const {pathMatcher, searchMatcherForSite, searchMatcherForLocale, localeMatcher} = getConfigMatcher(config);
	const pathMatch = pathname.match(pathMatcher);
	const localeMatch = pathname.match(localeMatcher);
	const searchMatchForSite = search.match(searchMatcherForSite);
	const searchMatchForLocale = search.match(searchMatcherForLocale);
	// the value can only either in the path or search query param, there will be no overridden
	const siteRef = pathMatch?.groups.site || searchMatchForSite?.groups.site;
	let localeRef = pathMatch?.groups.locale || searchMatchForLocale?.groups.locale;

	/** in case site have alias look like locale, we also check if current ref is also is locale
	 * as we can have only one siteRef or localeRef
	 */
	if (!localeRef && siteRef && localeMatch?.groups.locale) {
		localeRef = localeMatch.groups.locale;
	}

	return {siteRef, localeRef};
};

/**
 * This function return the site that correspond with provided site alias
 * @param {string} siteRef - site reference that was catch by regexp
 * @returns {String} - site id for the provided site alias or empty string
 */
export const matchSiteAliasPerID = (siteRef) => {
	if (!siteRef) {
		return '';
	}

	const sites = getSites();
	let siteAliasMatch = '';
	sites.forEach((site) => {
		if (!siteAliasMatch && siteRef.indexOf(site.alias) !== -1) {
			siteAliasMatch = site.id;
		}
	});

	return siteAliasMatch;
};

/**
 * This function returns the url config from the current configuration
 * @return {object} - url config
 */
export const getUrlConfig = () => {
	const {app} = getConfig();

	if (!app.url) {
		throw new Error('Cannot find `url` key. Please check your configuration file.');
	}

	return app.url;
};

/**
 * Given your application's configuration this function returns a set of regular expressions used to match the site
 * and locale references from an url.
 * @param config
 * @return {{searchMatcherForSite: RegExp, searchMatcherForLocale: RegExp, pathMatcher: RegExp}}
 */
export const getConfigMatcher = (config) => {
	if (!config) {
		throw new Error('Config is not defined.');
	}

	const allSites = getSites();
	const sites = [];
	allSites.forEach((site) => {
		if (site.alias) {
			sites.push('[\\w]{2}-' + site.alias); // en-us
			// eslint-disable-next-line
			sites.push(site.alias + '/[\\w]{2}'); // us/en
		}

		// Covers urls from sandbox
		sites.push(site.id);
	});

	const localePattern = `(?<locale>(([\\w]{2}-)?[\\w]{2})|default)`;
	//(?<locale>(\/([a-z]{2}-)?[a-z]{2}\/))

	// prettier-ignore
	// eslint-disable-next-line
	const searchPatternForSite = `site=(?<site>${sites.join('|')})`
	// prettier-ignore
	// eslint-disable-next-line
	const pathPattern = `(?:\/(?:s\/)?(?<site>${sites.join('|')})\/)?(?:\/${localePattern}\/)?(?!\/\\w)`
	// prettier-ignore
	// eslint-disable-next-line
	const searchPatternForLocale = `locale=${localePattern}`
	// prettier-ignore
	// eslint-disable-next-line
	const localeMatcher = new RegExp(`\/${localePattern}\/`);
	const pathMatcher = new RegExp(pathPattern, 'i');
	const searchMatcherForSite = new RegExp(searchPatternForSite);
	const searchMatcherForLocale = new RegExp(searchPatternForLocale);

	return {
		localeMatcher,
		pathMatcher,
		searchMatcherForSite,
		searchMatcherForLocale,
	};
};

/**
 * Determine the locale object from an url
 * If the localeRef is not found from the url, set it to default locale of the current site
 * and use it to find the locale object
 *
 * @param url
 * @return {Object} locale object
 */
export const resolveLocaleFromUrl = (url) => {
	if (!url) {
		throw new Error('URL is required to look for the locale object');
	}

	let {localeRef} = getParamsFromPath(url);
	const site = resolveSiteFromUrl(url);
	const {supportedLocales} = site.l10n;

	// if no localeRef is found, use the default value of the current site
	if (!localeRef) {
		localeRef = site.l10n.defaultLocale;
	}

	const locale = supportedLocales.find((locale) => locale.alias === localeRef || locale.id === localeRef);

	if (locale) {
		return locale;
	}

	// if locale is not defined, use default locale as fallback value
	const defaultLocale = site.l10n.defaultLocale;

	return supportedLocales.find((locale) => locale.alias === defaultLocale || locale.id === defaultLocale);
};

/**
 * Appends JS script tag to the DOM for third party integrations
 * @param {String} src Script scr
 * @param {String} id Script ID
 * @param {Object} dataAttributes Key-value pairs for data-XXX attributes
 * @param {Boolean|undefined} async script async attribute
 * @param {Object} attributes Key-value pairs for attributes Key-value pairs for attributes
 * @param {Object} customSettings Key-value pairs for type overriding, text content, in-head insertion and dynamic insertion
 * @returns {Promise}
 */
export const loadScript = (
	src,
	id,
	dataAttributes = {},
	async = false,
	attributes = {},
	customSettings = {typeOverride: '', textContent: '', isHead: false, isReinsertable: false},
) => {
	if (!id || !src) {
		throw new Error('Missing required parameters [ID|src]');
	}

	if (scriptLoadingPromises[id]) {
		return scriptLoadingPromises[id];
	}

	const promise = new Promise<void>(function (resolve, reject) {
		if (document.getElementById(id)) {
			resolve(undefined);
		} else {
			const s = createHTMLElement('script', {
				type: customSettings?.typeOverride || 'text/javascript',
				src: src,
				id: id,
				async: async,
			});

			if (customSettings?.textContent) {
				s.removeAttribute('src');
				s.textContent = customSettings.textContent;
			}

			Object.keys(dataAttributes).forEach((key) => {
				s.setAttribute('data-' + key, dataAttributes[key]);
			});

			Object.keys(attributes).forEach((key) => {
				s.setAttribute('data-' + key, attributes[key]);
			});

			s.onload = () => {
				delete scriptLoadingPromises[id];

				resolve(undefined);
			};

			s.onerror = () => {
				delete scriptLoadingPromises[id];

				reject();
			};

			if (customSettings?.isHead) {
				document.head.appendChild(s);
			} else {
				document.body.appendChild(s);
			}
		}
	});

	if (!customSettings?.isReinsertable) {
		scriptLoadingPromises[id] = promise;
	}

	return promise;
};

/**
 * Appends jQuery v3.7.1 script tag to the DOM for third party integrations
 * @param {Boolean|undefined} async script async attribute
 * @returns {Promise}
 */
export const loadJQuery = (async = false) => {
	return loadScript('//code.jquery.com/jquery-3.7.1.min.js', 'jQueryScript', {}, async, {
		integrity: 'sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo=',
		crossorigin: 'anonymous',
	});
};

/**
 * Remove DOM node from document
 * @param {String} id DOM Node ID
 * @param {Object} dataAttributes Key-value pairs for data-XXX attributes
 * @returns {boolean}
 */
export const removeDOMNode = (id, dataAttributes = {}) => {
	const documentElement = document.getElementById(id);

	if (documentElement) {
		const dataKeys = Object.keys(dataAttributes);
		let isDataAttributesTheSame = false;

		if (dataKeys.length) {
			isDataAttributesTheSame = dataKeys.every((key) => {
				let dataAttributeValue = documentElement.getAttribute(key);

				return dataAttributeValue === dataAttributes[key];
			});
		}

		if (!isDataAttributesTheSame) {
			if (documentElement.parentNode) {
				documentElement.parentNode.removeChild(documentElement);
			} else if (typeof documentElement.remove === 'function') {
				documentElement.remove();
			}

			return true;
		}
	}

	return false;
};

/**
 * Creates HTMLElement
 * @param {String} tagName - tag name
 * @param {Object} elementAttributes - element Attributes
 * @returns {HTMLElement} html element
 */
export const createHTMLElement = (tagName: string, elementAttributes: any = {}): HTMLElement => {
	const element = document.createElement(tagName);

	if (elementAttributes) {
		Object.keys(elementAttributes).forEach((elementAttributeKey) => {
			element[elementAttributeKey] = elementAttributes[elementAttributeKey];
		});
	}

	return element;
};

/**
 * Given a credit card number return the cardType o false
 * @param cardNumber - credit card number
 * @return {string|boolean}
 */
export const getCardType = (cardNumber) => {
	const cardType = {
		Diners: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
		Master: /^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/,
		Visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
		Amex: /^3[47][0-9]{13}$/,
		Discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
		JCB: /^(?:2131|1800|35\d{3})\d{11}$/,
		Maestro: /^(5018|5020|5038|5612|5893|6304|6759|6761|6762|6763|0604|6390)\d+$/,
	};

	for (var key in cardType) {
		if (cardType[key].test(cardNumber.toString())) {
			return key;
		}
	}

	return false;
};

/**
 * Returns countryCode for the given languageCode and host
 * @param {String} hostname Current hostname
 * @param {String} languageCode Current language code
 */
export const getMappedCountryCodeByLanguage = (hostname, languageCode) => {
	const {app} = getConfig();
	const {countryMappings} = getDefaultSite();
	let hostnameMappings = countryMappings[hostname] || countryMappings.default;

	return hostnameMappings[languageCode] || app.defaultCountry;
};

/**
 * @function
 * @description Sets <html> element language attributes
 * @param {String} languageCode Current language code
 * @param {String} languageDirection Current language direction
 * @returns {void}
 */
export const setRootElementLanguageAttributes = (languageCode: string, languageDirection: string) => {
	document.documentElement.setAttribute('lang', languageCode);
	document.documentElement.setAttribute('dir', languageDirection);
};

/**
 * @function
 * @description Returns Promise when vertical scroll is completed
 * @returns {Promise}
 */
export const onVerticalScrollEnd = (element: HTMLElement | null = null) => {
	const idleFramesThreshold = 5;
	let last_changed_frame = 0;
	let lastY = element ? element.scrollTop : window.scrollY;

	return new Promise((resolve) => {
		// eslint-disable-next-line require-jsdoc
		function tick(frames) {
			if (frames - last_changed_frame > idleFramesThreshold) {
				resolve(null);
			} else {
				const newY = element ? element.scrollTop : window.scrollY;

				if (newY != lastY) {
					last_changed_frame = frames;
					lastY = newY;
				}

				requestAnimationFrame(tick.bind(null, frames + 1));
			}
		}

		tick(0);
	});
};

/**
 * Creates a range
 * @param {Number} start - range start
 * @param {Number} end - range end
 */
export const createRange = (start, end) => {
	return Array.from({length: end}, (_, i) => i + 1);
};

/**
 * @function
 * @description Truncate text that contains <html> tags
 * @param {string} text to truncate
 * @param {number} limit of remaining symbols in text, {optional}
 * @returns {string} Returns truncated text, that might contain <html> tags
 */
export const truncateHtmlString = (text: string, limit?: number): string => {
	if (!limit) {
		return text;
	}

	// Regular expression to match all HTML tags and text content
	const regex = /(<\/?[^>]+(>|$))|([^<]+)/g;

	// Initialize the result string
	let result = '';

	// Initialize the character count
	let count = 0;

	// Variable to store matches from the regular expression
	let match: RegExpExecArray | null;
	const tagStack: string[] = [];

	// Iterate through all matches of HTML tags and text content
	while ((match = regex.exec(text)) !== null) {
		const tag = match[1];
		const text = match[3];

		if (tag) {
			// Check if the current tag is a closing tag
			const isClosingTag = tag.startsWith('</');
			const tagName = tag
				.replace(/^<\/?/, '')
				.replace(/>$/, '')
				.replace(/style=".*?"/gi, '')
				.trim();

			if (isClosingTag) {
				const lastOpeningTag = tagStack.pop();

				if (lastOpeningTag && lastOpeningTag === tagName) {
					result += tag;
				}
			} else {
				// Add the opening tag to the stack
				tagStack.push(tagName);

				result += tag;
			}
		} else if (text && count < limit) {
			// If the character count plus the length of the text exceeds the limit,
			// append the remaining allowed text to the result and update the count
			if (count + text.length > limit) {
				const remainingText = text.slice(0, limit - count);

				result += remainingText;
				count += remainingText.length;
			} else {
				// If the character count is still within the limit, append the text to the result and update the count
				result += text;
				count += text.length;
			}
		}

		// If the character count has reached the limit, break the loop
		if (count >= limit) {
			const dots = '...';

			result += dots;

			break;
		}
	}

	// Close any remaining unclosed tags
	while (tagStack.length > 0) {
		const tagName = tagStack.pop();

		if (tagName) {
			result += `</${tagName}>`;
		}
	}

	return result;
};

export const isNode =
	typeof process === 'object' && typeof process.versions === 'object' && typeof process.versions.node === 'string';

export const isBrowser = typeof window === 'object' && typeof window.document === 'object';

// eslint-disable-next-line no-undef
export const globalObject = isBrowser ? window : globalThis;

export const hasFetchAvailable = typeof globalObject.fetch === 'function';

const cacheablePaths = [
	'?routeInfo',
	'Configurations-Get',
	'Countries-GetAll',
	'Locales-GetAll',
	'Currencies-GetConversionRates',
	'Countries-Get',
];

const isRequestCacheable = (url: string, options: any) => {
	return (!options?.method || options.method === 'GET')
		&& url.indexOf('preview=true') === -1
		&& options?.headers?.['x-preview'] !== 'true'
		&& (!options?.headers?.['cache-control']
			|| (
				options?.headers?.['cache-control']?.indexOf('no-cache') === -1
				&& options?.headers?.['cache-control']?.indexOf('no-store') === -1
			));
};

const isResponseCacheable = (res: any) => {
	if (res.status >= 200 && res.status < 300) {
		for (let path of cacheablePaths) {
			if (res.url.indexOf(path) !== -1) {
				return true;
			}
		}
	}

	return false;
};

/**
 * Exports separated fetch for node and browser
 *
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
export const fetch = (() => {
	let currentFetch;
	let CurrentResponse; // TODO: replace CurrentResponse with response.clone() when will be fixed

	if (isNode) {
		({fetch: currentFetch, Response: CurrentResponse} = getServerFetch());
	}

	if (!currentFetch && !hasFetchAvailable) {
		throw new Error('Bad environment: it is not a node environment but fetch is not defined');
	}

	if (!isNode) {
		return globalObject.fetch;
	}

	return (url: string, options?: {}) => {
		return new Promise((resolve) => {
			const isThisRequestCacheable = isRequestCacheable(url, options);
			let cachedData: any;
			let key: string;

			if (isThisRequestCacheable) {
				key = url + (options ? JSON.stringify(options) : '');

				const cachedRecord = SSRCacheMgr.get(key);

				if (cachedRecord) {
					cachedData = cachedRecord;
				}
			}

			if (cachedData) {
				return resolve(
					new CurrentResponse(cachedData.text, {
						status: cachedData.status,
						statusText: cachedData.statusText,
						headers: cachedData.headers,
						url: url,
					}),
				);
			} else {
				return currentFetch(url, options)
					.then((res) =>
						res.text().then((text) => ({
							res,
							text,
						})),
					)
					.then(({res, text}) => {
						const originalResponse = new CurrentResponse(text, {
							status: res.status,
							statusText: res.statusText,
							headers: Object.fromEntries(res.headers.entries()),
							url: url,
						});

						if (isResponseCacheable(originalResponse)) {
							const expirationTime = parseInt(originalResponse.headers.get('x-sf-cc-expiration-time'));

							SSRCacheMgr.set(
								key,
								{
									status: res.status,
									statusText: res.statusText,
									headers: Object.fromEntries(res.headers.entries()),
									text: text,
								},
								isNaN(expirationTime) ? null : expirationTime
							);
						}

						resolve(originalResponse);
					});
			}
		});
	};
})();

/**
 * Encodes the given string in base64 ( works both with browser and node )
 * @param {String} sourceString - source string
 * @param encoding
 */
// eslint-disable-next-line no-undef
export const toBase64 = (sourceString: string, encoding: BufferEncoding = 'binary'): string => {
	if (typeof Buffer !== 'undefined') {
		return Buffer.from(sourceString, encoding).toString('base64');
	} else {
		return btoa(unescape(encodeURIComponent(sourceString)));
	}
};

/**
 * Decodes the given string from base64 ( works both with browser and node )
 * @param {String} sourceString - source string
 */
// eslint-disable-next-line no-undef
export const fromBase64 = (base64String: string, encoding: BufferEncoding = 'ascii') => {
	if (typeof Buffer !== 'undefined') {
		return Buffer.from(base64String, 'base64').toString(encoding);
	} else {
		return btoa(unescape(encodeURIComponent(base64String)));
	}
};

/**
 * Returns true if ApplePay is supported
 * @returns {boolean}
 */
export const isApplePaySupported = () => {
	return (
		typeof window !== 'undefined' &&
		(window as any).ApplePaySession &&
		(window as any).ApplePaySession.canMakePayments() &&
		document.location.protocol === 'https:' &&
		DeviceMgr.isSafari(navigator?.userAgent)
	);
};

/**
 * Returns true if GooglePay is supported
 * @returns {boolean}
 */
export const isGooglePaySupported = () => {
	return (
		typeof window !== 'undefined' &&
		document.location.protocol === 'https:' &&
		!DeviceMgr.isSafari(navigator?.userAgent)
	);
};

/**
 * Returns a promise that resolves after the given ms
 * @param {number} ms - milliseconds
 */
export const timeout = (ms): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Finds the greatest common divisor for two numbers
 * @param {Number} a - first number
 * @param {Number} b - second number
 * @returns {Number}
 */
const findGreatestCommonDivisor = function gcd(a: number, b: number): number {
	return b ? gcd(b, a % b) : a;
};

/**
 * Generates svg placeholder for the given size
 * @param {Number} width - width
 * @param {Number} height - height
 * @param {String} color - color
 * @returns {String}
 */
export const generateImagePlaceholder = (width: number, height: number, color: string = '#FFFFFF'): string => {
	const gcd = findGreatestCommonDivisor(width, height);
	const encodedColor = encodeURIComponent(color);

	return `data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20viewBox%3D'0%200%20${
		width / gcd
	}%20${height / gcd}'%20style%3D'background-color%3A${encodedColor}%3B'%2F%3E`;
};

const easingFunctions = {
	// no easing, no acceleration
	linear: (t: number) => t,
	// accelerating from zero velocity
	easeInQuad: (t: number) => t * t,
	// decelerating to zero velocity
	easeOutQuad: (t: number) => t * (2 - t),
	// acceleration until halfway, then deceleration
	easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
	// accelerating from zero velocity
	easeInCubic: (t: number) => t * t * t,
	// decelerating to zero velocity
	easeOutCubic: (t: number) => --t * t * t + 1,
	// acceleration until halfway, then deceleration
	easeInOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
	// accelerating from zero velocity
	easeInQuart: (t: number) => t * t * t * t,
	// decelerating to zero velocity
	easeOutQuart: (t: number) => 1 - --t * t * t * t,
	// acceleration until halfway, then deceleration
	easeInOutQuart: (t: number) => (t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t),
	// accelerating from zero velocity
	easeInQuint: (t: number) => t * t * t * t * t,
	// decelerating to zero velocity
	easeOutQuint: (t: number) => 1 + --t * t * t * t * t,
	// acceleration until halfway, then deceleration
	easeInOutQuint: (t: number) => (t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t),
};

/**
 * Custom Smooth ScrollTo for decrease default speed
 *
 * @param {React.RefObject<HTMLElement>} el
 * @param {number} pos
 * @param {number} startPos
 * @param {{offset = 0, delay = 0, direction = 'horizontal'}} config
 *
 */
export const smoothScrollTo = (
	el: React.RefObject<HTMLElement>,
	pos: number,
	startPos: number,
	{delay = 0, duration = 0, direction = 'horizontal'} = {},
	motion: keyof typeof easingFunctions = 'linear',
) => {
	if (pos === startPos) {
		return;
	}

	const keyScrollTo = direction === 'horizontal' ? 'left' : 'top';
	const difference = pos - startPos;
	let startTime;

	const step = () => {
		if (!el?.current?.scrollTo) {
			return;
		}

		if (!startTime) {
			startTime = performance.now();
		}

		const progress = (performance.now() - startTime) / duration;
		const amount = easingFunctions[motion](progress);

		el.current.scrollTo({[keyScrollTo]: startPos + amount * difference});

		if (progress < 0.99) {
			window.requestAnimationFrame(step);
		}
	};

	setTimeout(step, delay);
};

/**
 * Transforms DateTime to local
 * @param {Date} date - input date
 * @returns {String}
 */
export const dateToDateTimeLocal = (date: Date): string => {
	const ten = (i) => {
			return (i < 10 ? '0' : '') + i;
		},
		YYYY = date.getFullYear(),
		MM = ten(date.getMonth() + 1),
		DD = ten(date.getDate()),
		HH = ten(date.getHours()),
		II = ten(date.getMinutes()),
		SS = ten(date.getSeconds());
	return YYYY + '-' + MM + '-' + DD + 'T' + HH + ':' + II + ':' + SS;
};

/**
 * Clones the source object
 * @param {object} source - source object
 */
export const cloneObject = <T extends object>(source: T): T => {
	// @ts-ignore
	let result: T = {};

	if (typeof source === 'object') {
		try {
			result = structuredClone(source);
		} catch (e) {
			result = source;
		}
	}

	return result;
};

/**
 * Removes all null and undefined values from object
 * @param {Object} obj - input object
 */
export const cleanObject = (obj: object) => {
	return Object.fromEntries(
		Object.entries(obj)
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			.filter(([_, value]) => value !== null)
			.map(([key, value]) => [key, value === Object(value) ? cleanObject(value) : value]),
	);
};

/**
 * Removes \u0660 (٠) and \u066B (٫) arabic signs
 * @param {string} text - text
 */
export const cleanExtraSigns = (text: string) => text.replace(/[\u0660\u066B]/g, '');

/**
 * Removes all spaces from string
 * @param {string} str - input string
 * @returns {string} - string without spaces
 */
export const removeSpaces = (str: string): string => str.replace(/\s/g, '');

/**
 * Parses a boolean string to boolean
 * @param {string} value - input value
 * @returns {Boolean|null}
 */
export const parseBoolean = (value: string): boolean | null => {
	switch (value) {
		case 'true':
			return true;
		case 'false':
			return false;
		default:
			return null;
	}
};

/**
 * Parse a string containing the value of a CSS measure
 * into a numeric value in pixels
 * @param {string} value - input value
 * @returns {number}
 */
export const parseCSSPropertyValue = (value: string): number => {
	let totalPixels = 0;

	if (value) {
		const remInPixels = parseInt(window.getComputedStyle(document.documentElement).fontSize);

		const regex = /(\d+(\.\d+)?)(rem|px)/g;
		let match;

		while ((match = regex.exec(value)) !== null) {
			const value = parseFloat(match[1]);
			const unit = match[3];

			if (unit === 'rem') {
				totalPixels += value * remInPixels;
			} else if (unit === 'px') {
				totalPixels += value;
			}
		}
	}

	return totalPixels;
};

/**
 * Run a promise for few times until it returns success, or return error if all retries failed
 * @param {Function} promiseHandle - function to retry
 * @param {Number} retries - count of retries
 * @param {Object} loggerData - logger data
 * @returns {Promise} - promise
 */
export const promiseWithRetry = (promiseHandle, retries = 3, loggerData) => {
	return new Promise((resolve, reject) => {
		const attemptPromise = (attempt) => {
			promiseHandle()
				.then(response => {
					resolve(response);
				})
				.catch(error => {
					if (attempt < retries) {
						if (loggerData?.warningTitle) {
							loggerData.logProvider.warning(loggerData.warningTitle, error);
						}

						attemptPromise(attempt + 1);
					} else {
						if (loggerData?.errorTitle) {
							loggerData.logProvider.error(loggerData.errorTitle, error);
						}

						reject(error);
					}
				});
		};

		attemptPromise(1);
	});
};

/**
 * Generates a simple, INSECURE hash for non-cryptographic purposes
 * @param {String} string - source string
 */
export const stringToHash = (string: string): number => {
	let hash = 0;

	if (string.length === 0) {
		return hash;
	}

	for (let i = 0; i < string.length; i++) {
		const char = string.charCodeAt(i);

		hash = ((hash << 5) - hash + char) ^ (hash >> 2);
	}

	return Math.abs(hash);
};

export const replaceDISCacheVersion = (products: Product[]|ProductSearchHit[], path: string) => {
	const regex = /\/default\/[^/]+\/images\//g;
	const replacement = `/default/${path}/images/`;

	for (let i = 0; i < products.length; i++) {
		const product = products[i];

		if (!product.imageGroups) {
			continue;
		}

		for (let j = 0; j < product.imageGroups.length; j++) {
			const imageGroup = product.imageGroups[j];

			if (!imageGroup.images) {
				continue;
			}

			for (let k = 0; k < imageGroup.images.length; k++) {
				const image = imageGroup.images[k];

				if (image.link) {
					image.link = image.link.replace(regex, replacement);
				}

				if (image.disBaseLink) {
					image.disBaseLink = image.disBaseLink.replace(regex, replacement);
				}
			}
		}
	}
};

/**
 * Wrapper of Salesforce function that solves a bug on CSR Urls in local environment
 * @param  {String} asset - asset name
 */
export const getAssetUrl = (asset: string) => {
	return asset.startsWith('/mobify') ? asset : pwaKitGetAssetUrl(asset);
};

/**
 * Loads an image from a URL and returns a Promise that resolves with the loaded Image element.
 * @param {string} url - The URL of the image to load.
 * @returns {Promise<HTMLImageElement>} Promise that resolves with the loaded image.
 */
const loadImage = (url: string): Promise<HTMLImageElement> => {
	return new Promise((resolve, reject) => {
		const img = new Image();

		img.onload = () => resolve(img);
		img.onerror = reject;
		img.src = url;
	});
};

/**
 * Processes the image load queue, starting up to `maxConcurrentImagesLoads` image loads simultaneously.
 * Calls itself recursively to continue processing the queue.
 */
const processQueue = (): void => {
	if (currentImagesLoad >= maxConcurrentImagesLoads || imagesLoadQueue.length === 0) {
		return;
	}

	const {url, resolve, reject} = imagesLoadQueue.shift()!;

	currentImagesLoad++;

	loadImage(url)
		.then((img) => resolve(img))
		.catch(reject)
		.finally(() => {
			currentImagesLoad--;

			processQueue();
		});
};

/**
 * Adds an image URL to the preload queue and starts processing.
 * Limits the maximum number of concurrent image loads.
 *
 * @param {string} url - The URL of the image to preload.
 * @returns {Promise<HTMLImageElement>} Promise that resolves when the image has loaded.
 */
export const preloadImage = (url: string): Promise<HTMLImageElement> => {
	return new Promise((resolve, reject) => {
		imagesLoadQueue.push({url, resolve, reject});

		processQueue();
	});
};

/**
 * Compares two JavaScript objects for equality by converting them to JSON strings.
 * This method performs a shallow comparison and is not reliable if:
 * - The objects contain functions, undefined values, or symbols.
 * - The objects have different key orderings (even if logically equivalent).
 * - The objects include circular references (which would throw an error).
 *
 * @param {Object} o1 - The first object to compare.
 * @param {Object} o2 - The second object to compare.
 * @returns {boolean} Returns true if the objects are equal based on their JSON string representations, false otherwise.
 */
export const areObjectsEqual = (o1, o2) => {
	let result = false;

	try {
		result = JSON.stringify(o1) === JSON.stringify(o2);
	} catch (e) {
		// Handle potential circular references or serialization errors
	}

	return result;
};
